import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import Profile from "./Profile";
import Company from "./Company/Company";
import Branches from "./Branches/branches";
import { useMatch, useNavigate, useLocation } from "react-router-dom";
import ProfitCostTemplate from "./FixedHeaderSettings/FixedHeader";
import ContactLinkDataGrid from "pages/Settings/ContactLinksType";
import styles from "./Settings.module.scss";
import WorkType from "./WorkTypes/WorkTypes";
import Department from "./Departments/Departments";
import ActivityTypes from "./ActivityTypes/Activities";
import TimeTypes from "./TimeTypes/TimeTypes";
import UsersList from "./users/Userslist";
import Teams from "./Teams/Teams";
import SecurityProfiles from "./SecurityProfiles/SecurityProfiles";
import Integrations from "./Integrations/Integrations";
import Vatcodes from "./VAT/vat";
import Applications from "./Applications/Applications";
import Currency from "./Currency/currency";
import DefaultNominals from "./DefaultNominals";
import Sources from "./Sources/Sources";
import Referrers from "./Referrers/Referrer";
import {
  AccountsSectionItems,
  BillingSectionItems,
  CaseSectionItems,
  CompanySectionItems,
  MatterSectionItems,
  UsersSectionItems,
} from "components/SettingMenu/SettingMenuData";
import MatterSetup from "./Company/MatterSetup";
import SettingMenuItemId from "components/SettingMenu/SettingMenuItemId.ts";
import DocTypes from "./DocumentTypes/DocTypes";
import RiskAssessment from "./RiskAssessment";
import UserRates from "./UserRates/UserRates";
import BillingTemplates from "./BillingTemplates";
import ChartofAccounts from "../ChartAccounts/ChartofAccounts";
import Authorizations from "./Authorizations";
import { useAppViewMode } from "hooks/useAppViewMode";
import { VIEW_MODES } from "CONSTANT/ViewModes/AppViewModes";
import SupplierCategories from "./Suppliers/SupplierCategories";
import InterestCalculation from "./InterestCalculation";
import { useCompany } from "services/companyService";
import { ExpansionPanel, ExpansionPanelContent } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import Notifications from "components/layout/Notifications";
import { ProfileBox } from "components/layout/ProfileBox";
import { BsBookmarkStar, BsBookmarkStarFill } from "react-icons/bs";
import { IoChevronBack } from "react-icons/io5";
import { HiOutlineHome } from "react-icons/hi";
import SearchTextBox from "components/SearchComponent";
import linqLogo from "assets/Logos/linqlawhublogo.svg";

const FAVOURITES_KEY = "linq_setting_favourites";

const DEFAULT_FAVOURITES = [
  SettingMenuItemId.ChartOfAccounts,
  SettingMenuItemId.Departments,
  SettingMenuItemId.Worktypes,
  SettingMenuItemId.Sources,
  SettingMenuItemId.Referrers,
];

const buildAllSections = (accountsMenuItems) => [
  { key: "company", label: "Company", items: CompanySectionItems },
  { key: "user", label: "User", items: UsersSectionItems },
  { key: "matter", label: "Matter Management", items: MatterSectionItems },
  { key: "case", label: "Case Management", items: CaseSectionItems },
  { key: "accounts", label: "Accounts", items: accountsMenuItems },
  { key: "billing", label: "Billings", items: BillingSectionItems },
];

const Options = () => {
  const { data: company } = useCompany();
  const companyFetched = useSelector((state) => state.company.fetched);
  const multiCurrencyModuleOn = Boolean(
    company?.modules?.MultiCurrency ?? company?.Modules?.MultiCurrency,
  );
  const showMultiCurrencyUi =
    !companyFetched || multiCurrencyModuleOn;
  const accountsMenuItems = React.useMemo(
    () =>
      showMultiCurrencyUi
        ? AccountsSectionItems
        : AccountsSectionItems.filter((i) => i.id !== SettingMenuItemId.Currencies),
    [showMultiCurrencyUi],
  );
  const allSections = React.useMemo(
    () => buildAllSections(accountsMenuItems),
    [accountsMenuItems],
  );
  const allItems = React.useMemo(
    () => allSections.flatMap((s) => s.items),
    [allSections],
  );

  const [openOption, setOpenOption] = useState(-1);
  const [shownewusers, setshownewusers] = useState(false);
  const [shownewbranches, setshownewbranches] = useState(false);
  const [shownewVat, setshownewVat] = useState(false);
  const [shownewCurrency, setshownewCurrency] = useState(false);
  const [showNewTemplate, setshowNewTemplate] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    company: true, user: false, matter: false,
    case: false, accounts: false, billing: false,
  });
  const [favourites, setFavourites] = useState(() => {
    try {
      const saved = localStorage.getItem(FAVOURITES_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_FAVOURITES;
    } catch {
      return DEFAULT_FAVOURITES;
    }
  });

  const { appViewMode } = useAppViewMode();
  const params = useMatch("/settings/:data");
  const navigate = useNavigate();
  const location = useLocation();
  const settingsState = { state: location.state };

  const urlDirector = useCallback(() => {
    let matched = false;
    allItems.forEach((item) => {
      if (item?.route?.toLowerCase() === `/${params?.params?.data?.toLowerCase()}`) {
        setOpenOption(item.id);
        matched = true;
      }
    });
    if (!matched) setOpenOption(0);
  }, [params, allItems]);

  useEffect(() => {
    if (params?.params?.data != null) urlDirector();
    else setOpenOption(-1);
  }, [params, urlDirector]);

  useEffect(() => {
    if (!companyFetched) {
      return;
    }
    if (multiCurrencyModuleOn) {
      return;
    }
    if (openOption !== SettingMenuItemId.Currencies) {
      return;
    }
    navigate("/settings", { state: location.state });
    setOpenOption(-1);
  }, [companyFetched, multiCurrencyModuleOn, openOption, navigate, location.state]);

  const saveFavourites = (newFavs) => {
    localStorage.setItem(FAVOURITES_KEY, JSON.stringify(newFavs));
    setFavourites(newFavs);
  };

  const toggleFavourite = (e, itemId) => {
    e.stopPropagation();
    saveFavourites(
      favourites.includes(itemId)
        ? favourites.filter((id) => id !== itemId)
        : [...favourites, itemId]
    );
  };

  const toggleSection = (key) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const onClose = () => {
    setshownewusers(false);
    setshownewbranches(false);
    setshownewVat(false);
    setshownewCurrency(false);
    setshowNewTemplate(false);
    navigate("/settings", settingsState);
  };

  const handleExitSettings = () => {
    if (appViewMode === VIEW_MODES.OUTLOOK) { window.history.back(); return; }
    const returnPath = location.state?.from;
    if (returnPath && !returnPath.startsWith("/settings")) {
      navigate(returnPath);
    } else {
      navigate("/");
    }
  };

  const favouriteItems = allItems.filter((item) => favourites.includes(item.id));

  const searchResults = searchQuery.trim()
    ? allItems.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : null;

  const showHome = openOption === -1 || openOption === 0;
  const activeItem = allItems.find((item) => item.id === openOption);
  const activeSection = allSections.find((s) => s.items.some((i) => i.id === openOption));

  const componentToRender = () => {
    switch (openOption) {
      case SettingMenuItemId.Profile: return <Profile component={"Option"} />;
      case SettingMenuItemId.Company: return <Company onCancel={onClose} />;
      case SettingMenuItemId.Users: return <UsersList open={() => setshownewusers(true)} onClose={shownewusers} onCancel={onClose} />;
      case SettingMenuItemId.Branches: return <Branches open={() => setshownewbranches(true)} onClose={shownewbranches} />;
      case SettingMenuItemId.Defaultnominals: return <DefaultNominals />;
      case SettingMenuItemId.Vat: return <Vatcodes open={() => setshownewVat(true)} onClose={shownewVat} />;
      case SettingMenuItemId.Currencies: return <Currency open={() => setshownewCurrency(true)} onClose={shownewCurrency} onCancel={onClose} />;
      case SettingMenuItemId.ChartOfAccounts: return <ChartofAccounts />;
      case SettingMenuItemId.SupplierCategories: return <SupplierCategories />;
      case SettingMenuItemId.InterestCalculation: return <InterestCalculation />;
      case SettingMenuItemId.FixedFeeBilling: return <ProfitCostTemplate open={() => setshowNewTemplate(true)} onClose={showNewTemplate} onCancel={onClose} />;
      case SettingMenuItemId.Applications: return <Applications />;
      case SettingMenuItemId.Contactcategories: return <ContactLinkDataGrid />;
      case SettingMenuItemId.Worktypes: return <WorkType open={() => setshownewCurrency(true)} onClose={shownewCurrency} onCancel={onClose} />;
      case SettingMenuItemId.Departments: return <Department />;
      case SettingMenuItemId.Activitytypes: return <ActivityTypes open={() => setshownewCurrency(true)} onClose={shownewCurrency} onCancel={onClose} />;
      case SettingMenuItemId.Timetypes: return <TimeTypes open={() => setshownewCurrency(true)} onClose={shownewCurrency} onCancel={onClose} />;
      case SettingMenuItemId.Teams: return <Teams />;
      case SettingMenuItemId.SecurityProfiles: return <SecurityProfiles />;
      case SettingMenuItemId.Integrations: return <Integrations />;
      case SettingMenuItemId.Sources: return <Sources />;
      case SettingMenuItemId.Referrers: return <Referrers />;
      case SettingMenuItemId.MatterSettings: return <MatterSetup />;
      case SettingMenuItemId.DocumentRegisterTypes: return <DocTypes />;
      case SettingMenuItemId.RiskAssessmentSettings: return <RiskAssessment />;
      case SettingMenuItemId.UserRates: return <UserRates />;
      case SettingMenuItemId.BillingTemplates: return <BillingTemplates type={"BillingTemplate"} />;
      case SettingMenuItemId.Authorisations: return <Authorizations />;
      default: return null;
    }
  };

  return (
    <div className="d-flex flex-column h-100 w-100 overflow-hidden">
      <header className={`${styles["settings-header"]} d-flex align-items-center bg-white justify-content-between px-5 pt-1`}>
        <img src={linqLogo} alt="Linq Law Hub" style={{ height: 40 }} className="cursor-pointer" onClick={() => navigate("/")} />
        <SearchTextBox
          changeFilterValue={(val) => {
            const v = val ?? "";
            setSearchQuery(v);
            if (v.trim() && !showHome) navigate("/settings", settingsState);
          }}
          width="485px"
        />
        <div className="border-1 border rounded-2">
          <Notifications />
        </div>
      </header>

      <div className="d-flex flex-grow-1 overflow-hidden">
        <aside className={`${styles["settings-sidebar"]} ps-3 d-flex flex-column h-100 flex-shrink-0`}>
          <div className={`${styles["sidebar-scroll"]} flex-grow-1`}>
            <Button
              className="justify-content-start fw-bold p-3 cursor-pointer border-0 shadow-none w-fit-content btn"
              style={{ fontSize: '16px', fontWeight: '700' }}
              onClick={handleExitSettings}
            >
              <IoChevronBack size={18} className="me-2" style={{ marginBottom: '2px' }} />
              Exit Settings
            </Button>
            <nav className="ms-3">
              {allSections.map((section) => (
                <ExpansionPanel
                  key={section.key}
                  title={section.label}
                  expanded={expandedSections[section.key]}
                  onAction={() => toggleSection(section.key)}
                  className={styles["expansion-panel"]}
                >
                  {expandedSections[section.key] && (
                    <ExpansionPanelContent>
                      <div className="p-2">
                        {section.items.map((item) => (
                          <SidebarItem
                            key={item.id}
                            item={item}
                            isActive={openOption === item.id}
                            isFavourite={favourites.includes(item.id)}
                            onToggleFavourite={toggleFavourite}
                            onClick={() => navigate(`/settings${item.route}`, settingsState)}
                          />
                        ))}
                      </div>
                    </ExpansionPanelContent>
                  )}
                </ExpansionPanel>
              ))}
            </nav>
          </div>
          <div className="pb-2">
            <hr className="w-100 m-auto p-1" />
            <ProfileBox />
          </div>
        </aside>

        <main className={`${styles["settings-content"]} flex-grow-1`}>
          {showHome ? (
            searchResults ? (
              <div className={styles["content-inner"]}>
                <h2 className={`${styles["content-title"]}`}>
                  Search Results
                </h2>
                {searchResults.length === 0 ? (
                  <p className={styles["empty-text"]}>
                    No settings found for &ldquo;{searchQuery}&rdquo;
                  </p>
                ) : (
                  <div className={styles["items-grid"]}>
                    {searchResults.map((item) => (
                      <FavouriteCard
                        key={item.id}
                        item={item}
                        isFavourite={favourites.includes(item.id)}
                        onToggleFavourite={toggleFavourite}
                        onClick={() => navigate(`/settings${item.route}`, settingsState)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h2 className={`${styles["content-title"]}`}>Favourites</h2>
                {favouriteItems.length === 0 ? (
                  <p className={styles["empty-text"]}>
                    No favourites yet. Click the bookmark icon on any setting to add it here.
                  </p>
                ) : (
                  <div className={styles["items-grid"]}>
                    {favouriteItems.map((item) => (
                      <FavouriteCard
                        key={item.id}
                        item={item}
                        isFavourite={true}
                        onToggleFavourite={toggleFavourite}
                        onClick={() => navigate(`/settings${item.route}`, settingsState)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="d-flex flex-column h-100">
              <div className={`${styles["content-title"]} d-flex align-items-center`}>
                <Button
                  fillMode="flat"
                  className={styles["home-btn"]}
                  onClick={() => navigate("/settings", settingsState)}
                  title="Settings Home"
                >
                  <HiOutlineHome size={18} className="mb-1 -me-2" style={{ marginBottom: '2px' }} />
                </Button>
                {activeSection && (
                  <span >/ {activeSection.label}</span>
                )}
                {activeItem && (
                  <span className="mx-2">/ {activeItem.name}</span>
                )}
                {activeItem && (
                  <Button
                    fillMode="flat"
                    className={`${styles["fav-btn"]} ${favourites.includes(activeItem.id) ? styles["fav-btn-active"] : styles["fav-btn-visible"]}`}
                    onClick={(e) => toggleFavourite(e, activeItem.id)}
                    title={favourites.includes(activeItem.id) ? "Remove from favourites" : "Add to favourites"}
                  >
                    {favourites.includes(activeItem.id)
                      ? <BsBookmarkStarFill size={20} color="#FFC107" />
                      : <BsBookmarkStar size={20} color="#aaa" />}
                  </Button>
                )}
              </div>
              <div className="flex-grow-1 overflow-hidden" style={{ padding: "1rem 2.7rem" }}>
                {componentToRender()}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Options;

const SidebarItem = ({ item, isActive, isFavourite, onToggleFavourite, onClick }) => (
  <div
    className={`d-flex align-items-center gap-2 p-2 cursor-pointer ${styles["sidebar-item"]} ${isActive ? styles["sidebar-item-active"] : ""}`}
    onClick={onClick}
  >
    <img src={item.icon} alt={item.name} className={`flex-shrink-0 ${styles["sidebar-item-icon"]}`} />
    <span className={`flex-grow-1 ${styles["sidebar-item-label"]}`}>{item.name}</span>
    <Button
      fillMode="flat"
      className={`${styles["fav-btn"]} ${isFavourite ? styles["fav-btn-active"] : ""} flex-shrink-0`}
      onClick={(e) => onToggleFavourite(e, item.id)}
      title={isFavourite ? "Remove from favourites" : "Add to favourites"}
    >
      {isFavourite
        ? <BsBookmarkStarFill size={15} color="#FFC107" />
        : <BsBookmarkStar size={15} color="#aaa" />}
    </Button>
  </div>
);

const FavouriteCard = ({ item, isFavourite, onToggleFavourite, onClick }) => (
  <div
    className={`d-flex align-items-center gap-2 bg-white cursor-pointer ${styles["fav-card"]}`}
    onClick={onClick}
  >
    <img src={item.icon} alt={item.name} className={`flex-shrink-0 ${styles["fav-card-icon"]}`} />
    <span className={`flex-grow-1 ${styles["fav-card-label"]}`}>{item.name}</span>
    <Button
      fillMode="flat"
      className={`${styles["fav-btn"]} ${isFavourite ? styles["fav-btn-active"] : ""} flex-shrink-0`}
      onClick={(e) => onToggleFavourite(e, item.id)}
      title={isFavourite ? "Remove from favourites" : "Add to favourites"}
    >
      {isFavourite
        ? <BsBookmarkStarFill size={15} color="#FFC107" />
        : <BsBookmarkStar size={15} color="#aaa" />}
    </Button>
  </div>
);
